import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Close, Edit, Delete, AttachMoney } from '@mui/icons-material';
import {
  createLabel,
  updateLabel,
  deleteLabel,
  useWatchlistLabels,
  LABEL_COLORS
} from '../services/api';

/**
 * LabelManagerModal - Modal for creating, editing, and deleting labels
 *
 * @param {Object} props
 * @param {string} props.userEmail - User's email
 * @param {function} props.onClose - Called when modal is closed
 * @param {string} props.initialMode - 'list' | 'create' (default: 'list')
 */
const LabelManagerModal = ({ userEmail, onClose, initialMode = 'list' }) => {
  const queryClient = useQueryClient();
  const { data: labelsData = { labels: [], assignments: {} } } = useWatchlistLabels(userEmail);

  const [mode, setMode] = useState(initialMode); // 'list', 'create', 'edit'
  const [editingLabel, setEditingLabel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: LABEL_COLORS[0].value,
    trackPurchases: false
  });
  const [error, setError] = useState('');

  // Mutations
  const createMutation = useMutation({
    mutationFn: createLabel,
    onSuccess: () => {
      queryClient.invalidateQueries(['watchlistLabels', userEmail]);
      setFormData({ name: '', color: LABEL_COLORS[0].value, trackPurchases: false });
      setMode('list');
      setError('');
    },
    onError: (err) => setError(err.message)
  });

  const updateMutation = useMutation({
    mutationFn: updateLabel,
    onSuccess: () => {
      queryClient.invalidateQueries(['watchlistLabels', userEmail]);
      setEditingLabel(null);
      setMode('list');
      setError('');
    },
    onError: (err) => setError(err.message)
  });

  const deleteMutation = useMutation({
    mutationFn: deleteLabel,
    onSuccess: () => {
      queryClient.invalidateQueries(['watchlistLabels', userEmail]);
    },
    onError: (err) => setError(err.message)
  });

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Label name is required');
      return;
    }
    createMutation.mutate(formData);
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Label name is required');
      return;
    }
    updateMutation.mutate({
      labelId: editingLabel.id,
      ...formData
    });
  };

  const handleEdit = (label) => {
    setEditingLabel(label);
    setFormData({
      name: label.name,
      color: label.color,
      trackPurchases: label.trackPurchases
    });
    setMode('edit');
    setError('');
  };

  const handleDelete = (label) => {
    if (window.confirm(`Delete label "${label.name}"? This will remove it from all stocks.`)) {
      deleteMutation.mutate(label.id);
    }
  };

  const handleBack = () => {
    setMode('list');
    setEditingLabel(null);
    setFormData({ name: '', color: LABEL_COLORS[0].value, trackPurchases: false });
    setError('');
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;

  // Count stocks per label
  const getLabelCount = (labelId) => {
    return Object.values(labelsData.assignments || {}).filter(
      (a) => a.labels?.includes(labelId)
    ).length;
  };

  const renderForm = (isEdit = false) => (
    <form onSubmit={isEdit ? handleUpdateSubmit : handleCreateSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Label Name
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Tech, Dividend, IRA"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          maxLength={30}
          autoFocus
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Color
        </label>
        <div className="flex flex-wrap gap-2">
          {LABEL_COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setFormData({ ...formData, color: c.value })}
              className={`
                w-8 h-8 rounded-full transition-all
                ${formData.color === c.value ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : 'hover:scale-105'}
              `}
              style={{ backgroundColor: c.value }}
              title={c.name}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="trackPurchases"
          checked={formData.trackPurchases}
          onChange={(e) => setFormData({ ...formData, trackPurchases: e.target.checked })}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="trackPurchases" className="text-sm text-gray-700 flex items-center gap-1">
          <AttachMoney style={{ fontSize: 16, color: '#10B981' }} />
          Track purchases (date, price, shares)
        </label>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-200">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={handleBack}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : isEdit ? 'Update Label' : 'Create Label'}
        </button>
      </div>
    </form>
  );

  const renderList = () => (
    <div className="space-y-2">
      {labelsData.labels.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No labels yet</p>
          <p className="text-sm mt-1">Create labels to organize your watchlist</p>
        </div>
      ) : (
        <div className="max-h-[300px] overflow-y-auto space-y-1">
          {labelsData.labels.map((label) => (
            <div
              key={label.id}
              className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-50 group"
            >
              <span
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: label.color }}
              />
              <span className="flex-1 text-sm font-medium">{label.name}</span>
              <span className="text-xs text-gray-400">{getLabelCount(label.id)} stocks</span>
              {label.trackPurchases && (
                <AttachMoney style={{ fontSize: 14, color: '#10B981' }} title="Tracking purchases" />
              )}
              <button
                type="button"
                onClick={() => handleEdit(label)}
                className="p-1 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Edit label"
              >
                <Edit style={{ fontSize: 16 }} />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(label)}
                disabled={deleteMutation.isLoading}
                className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete label"
              >
                <Delete style={{ fontSize: 16 }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-200">
          {error}
        </div>
      )}

      <div className="pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={() => setMode('create')}
          disabled={labelsData.labels.length >= 20}
          className="w-full px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {labelsData.labels.length >= 20 ? 'Maximum 20 labels' : 'Create New Label'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-80">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900">
          {mode === 'create' && 'Create Label'}
          {mode === 'edit' && 'Edit Label'}
          {mode === 'list' && 'Manage Labels'}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
        >
          <Close style={{ fontSize: 20 }} />
        </button>
      </div>

      <div className="p-4">
        {mode === 'list' && renderList()}
        {mode === 'create' && renderForm(false)}
        {mode === 'edit' && renderForm(true)}
      </div>
    </div>
  );
};

export default LabelManagerModal;
